import React, { useState, useEffect, useCallback } from 'react';
import {
  ArrowRight,
  Search,
  Eye,
  DollarSign,
  Clock,
  CheckCircle,
  XCircle,
  X,
  Copy,
  Receipt,
  Download
} from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import { useToast } from '../contexts/ToastContext';
import NewtonsCradleLoader from '../components/common/NewtonsCradleLoader';
import apiService from '../utils/apiService';
import { useFetchOnceByKey } from '../hooks/useMountOnce';

const AffiliateTransfer = () => {
  const { isDarkMode } = useTheme();
  const { showSuccess, showError } = useToast();
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('sent'); // 'sent' or 'receive'
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [transferData, setTransferData] = useState({
    recipientEmail: '',
    amount: '',
    description: '',
    transferReason: '',
    transferType: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [transfers, setTransfers] = useState([]);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    pageSize: 10,
    totalCount: 0,
    totalPages: 0,
    hasNextPage: false,
    hasPreviousPage: false
  });
  const [isLoadingTransfers, setIsLoadingTransfers] = useState(false);
  const [availableBalance, setAvailableBalance] = useState(0);
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const [selectedTransfer, setSelectedTransfer] = useState(null);
  const [transferPage, setTransferPage] = useState(1);
  const [transferRefresh, setTransferRefresh] = useState(0);

  const transfersFetchKey = `${activeTab}|${searchTerm.trim()}|${transferPage}|${transferRefresh}`;

  const fetchTransfers = useCallback(async ({ cancelled }) => {
    setIsLoadingTransfers(true);
    try {
      const response = await apiService.getInternalTransferList({
        pageNumber: transferPage,
        pageSize: 10,
        searchTerm: searchTerm.trim() || '',
        type: activeTab === 'sent' ? 'S' : 'R',
      });

      if (cancelled()) return;

      if (response.success && response.data) {
        setTransfers(response.data.transfers || []);
        setPagination((prev) => response.data.pagination || prev);
        setAvailableBalance(response.data.availableBalance || 0);
      }
    } catch {
      if (!cancelled()) showError('Failed to load transfers');
    } finally {
      if (!cancelled()) setIsLoadingTransfers(false);
    }
  }, [activeTab, searchTerm, transferPage, showError]);

  useFetchOnceByKey(transfersFetchKey, fetchTransfers, true);

  useEffect(() => {
    setTransferPage(1);
  }, [activeTab, searchTerm]);

  // No frontend filtering needed - API should return correct data based on type
  const filteredTransfers = transfers;

  // Calculate dynamic stats from transfers data based on current tab
  const calculateStats = () => {
    if (activeTab === 'sent') {
      // For Sent tab, show stats for all sent transfers
      const totalAmount = transfers.reduce((sum, transfer) => sum + (transfer.transferAmount || 0), 0);
      const count = transfers.length;
      
      return {
        totalSentAmount: totalAmount,
        totalReceiveAmount: 0,
        sentCount: count,
        receiveCount: 0
      };
    } else {
      // For Receive tab, show stats for all receive transfers
      const totalAmount = transfers.reduce((sum, transfer) => sum + (transfer.transferAmount || 0), 0);
      const count = transfers.length;
      
      return {
        totalSentAmount: 0,
        totalReceiveAmount: totalAmount,
        sentCount: 0,
        receiveCount: count
      };
    }
  };

  const stats = calculateStats();

  // Copy transaction number to clipboard
  const copyTransactionNumber = async (transactionNumber) => {
    try {
      await navigator.clipboard.writeText(transactionNumber);
      showSuccess('Transaction number copied to clipboard');
    } catch (error) {
      // Error handling
      showError('Failed to copy transaction number');
    }
  };

  // Handle view receipt
  const handleViewReceipt = (transfer) => {
    setSelectedTransfer(transfer);
    setShowReceiptModal(true);
  };

  const handleCloseReceiptModal = () => {
    setShowReceiptModal(false);
    setSelectedTransfer(null);
  };

  // Download receipt as image
  const downloadReceipt = async (transfer) => {
    try {
      // Create a canvas element
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      // Set canvas size
      canvas.width = 400;
      canvas.height = 650; // Increased height for logo
      
      // Set background
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      
      // Load and draw logo
      const logo = new Image();
      logo.crossOrigin = 'anonymous';
      
      logo.onload = () => {
        // Draw logo at the top
        const logoSize = 40;
        const logoX = (canvas.width - logoSize) / 2;
        const logoY = 20;
        
        ctx.drawImage(logo, logoX, logoY, logoSize, logoSize);
        
        // Set text properties
        ctx.fillStyle = '#000000';
        ctx.font = 'bold 18px Arial';
        ctx.textAlign = 'center';
        
        // Draw header below logo
        ctx.fillText('PAYMENT TRANSFER RECEIPT', canvas.width / 2, logoY + logoSize + 30);
        ctx.font = '12px Arial';
        ctx.fillText('Transaction Confirmation', canvas.width / 2, logoY + logoSize + 50);
        
        drawReceiptContent(ctx, canvas, transfer, logoY + logoSize + 70);
      };
      
      logo.onerror = () => {
        // If logo fails to load, draw without it
        ctx.fillStyle = '#000000';
        ctx.font = 'bold 18px Arial';
        ctx.textAlign = 'center';
        
        ctx.fillText('PAYMENT TRANSFER RECEIPT', canvas.width / 2, 40);
        ctx.font = '12px Arial';
        ctx.fillText('Transaction Confirmation', canvas.width / 2, 60);
        
        drawReceiptContent(ctx, canvas, transfer, 80);
      };
      
      // Try to load logo from public folder
      logo.src = '/images/logo-Black.png';
      
    } catch (error) {
      // Error handling
      showError('Failed to download receipt');
    }
  };

  // Helper function to draw receipt content
  const drawReceiptContent = (ctx, canvas, transfer, startY) => {
    try {
      // Draw separator line
      ctx.strokeStyle = '#cccccc';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(50, startY);
      ctx.lineTo(canvas.width - 50, startY);
      ctx.stroke();
      
      // Draw amount
      ctx.fillStyle = '#000000';
      ctx.font = 'bold 24px Arial';
      ctx.fillText(`$${transfer.transferAmount?.toFixed(2)}`, canvas.width / 2, startY + 40);
      ctx.font = '10px Arial';
      ctx.fillText('TRANSFER AMOUNT', canvas.width / 2, startY + 60);
      
      // Draw status
      ctx.font = 'bold 12px Arial';
      const statusColor = transfer.status === 'Approved' ? '#22c55e' : 
                         transfer.status === 'Pending' ? '#eab308' : '#8b0000';
      ctx.fillStyle = statusColor;
      ctx.fillText(transfer.status, canvas.width / 2, startY + 85);
      
      // Draw separator
      ctx.strokeStyle = '#cccccc';
      ctx.beginPath();
      ctx.moveTo(50, startY + 105);
      ctx.lineTo(canvas.width - 50, startY + 105);
      ctx.stroke();
      
      // Draw transaction details
      ctx.fillStyle = '#000000';
      ctx.font = 'bold 12px Arial';
      ctx.textAlign = 'left';
      
      let yPos = startY + 130;
      const lineHeight = 20;
      
      // Transaction ID
      ctx.fillText('Transaction ID:', 50, yPos);
      ctx.font = '11px monospace';
      ctx.fillText(transfer.transactionNumber, 50, yPos + lineHeight);
      
      yPos += lineHeight * 2;
      
      // Date & Time
      ctx.font = 'bold 12px Arial';
      ctx.fillText('Date & Time:', 50, yPos);
      ctx.font = '11px Arial';
      ctx.fillText(`${new Date(transfer.createdDateTime).toLocaleDateString()} ${new Date(transfer.createdDateTime).toLocaleTimeString()}`, 50, yPos + lineHeight);
      
      yPos += lineHeight * 2.5;
      
      // Draw separator
      ctx.strokeStyle = '#cccccc';
      ctx.beginPath();
      ctx.moveTo(50, yPos);
      ctx.lineTo(canvas.width - 50, yPos);
      ctx.stroke();
      
      yPos += 20;
      
      // Transfer details
      ctx.font = 'bold 12px Arial';
      ctx.fillText('Transfer Type:', 50, yPos);
      ctx.font = '11px Arial';
      ctx.fillText(activeTab === 'sent' ? 'Sent' : 'Received', 50, yPos + lineHeight);
      
      yPos += lineHeight * 2;
      
      ctx.font = 'bold 12px Arial';
      ctx.fillText('From:', 50, yPos);
      ctx.font = '11px Arial';
      ctx.fillText(transfer.fromTransferType, 50, yPos + lineHeight);
      
      yPos += lineHeight * 2;
      
      ctx.font = 'bold 12px Arial';
      ctx.fillText('To:', 50, yPos);
      ctx.font = '11px Arial';
      ctx.fillText(transfer.toTransferType, 50, yPos + lineHeight);
      
      yPos += lineHeight * 2;
      
      ctx.font = 'bold 12px Arial';
      ctx.fillText('Reason:', 50, yPos);
      ctx.font = '11px Arial';
      ctx.fillText(transfer.transferReason, 50, yPos + lineHeight);
      
      yPos += lineHeight * 2;
      
      ctx.font = 'bold 12px Arial';
      ctx.fillText('Recipient:', 50, yPos);
      ctx.font = '11px Arial';
      ctx.fillText(transfer.recipientName || 'N/A', 50, yPos + lineHeight);
      ctx.fillText(transfer.recipientEmail || 'N/A', 50, yPos + lineHeight * 2);
      
      yPos += lineHeight * 3;
      
      if (transfer.transferRemarks) {
        ctx.font = 'bold 12px Arial';
        ctx.fillText('Description:', 50, yPos);
        ctx.font = '11px Arial';
        const words = transfer.transferRemarks.split(' ');
        let line = '';
        let lineY = yPos + lineHeight;
        
        for (let word of words) {
          const testLine = line + word + ' ';
          const metrics = ctx.measureText(testLine);
          if (metrics.width > canvas.width - 100 && line !== '') {
            ctx.fillText(line, 50, lineY);
            line = word + ' ';
            lineY += lineHeight;
          } else {
            line = testLine;
          }
        }
        ctx.fillText(line, 50, lineY);
        yPos = lineY + lineHeight;
      }
      
      yPos += 20;
      
      // Draw final separator
      ctx.strokeStyle = '#cccccc';
      ctx.beginPath();
      ctx.moveTo(50, yPos);
      ctx.lineTo(canvas.width - 50, yPos);
      ctx.stroke();
      
      yPos += 20;
      
      // Footer
      ctx.fillStyle = '#666666';
      ctx.font = '10px Arial';
      ctx.textAlign = 'center';
      ctx.fillText('Thank you for using our service', canvas.width / 2, yPos);
      ctx.fillText('Keep this receipt for your records', canvas.width / 2, yPos + 15);
      ctx.fillText(`Generated on: ${new Date().toLocaleDateString()} ${new Date().toLocaleTimeString()}`, canvas.width / 2, yPos + 35);
      
      // Convert canvas to blob and download
      canvas.toBlob((blob) => {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `transfer-receipt-${transfer.transactionNumber}.png`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        
        showSuccess('Receipt image downloaded successfully');
      }, 'image/png');
      
    } catch (error) {
      // Error handling
      showError('Failed to generate receipt');
    }
  };

  const getStatusBadge = (status) => {
    const statusStyles = {
      approved: 'bg-green-100 text-green-800',
      pending: 'bg-yellow-100 text-yellow-800',
      rejected: 'bg-red-100 text-red-800',
      failed: 'bg-red-100 text-red-800'
    };
    
    const statusIcons = {
      approved: <CheckCircle className="h-3 w-3" />,
      pending: <Clock className="h-3 w-3" />,
      rejected: <XCircle className="h-3 w-3" />,
      failed: <XCircle className="h-3 w-3" />
    };

    const statusKey = status?.toLowerCase() || 'pending';

    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full flex items-center space-x-1 ${statusStyles[statusKey] || statusStyles.pending}`}>
        {statusIcons[statusKey] || statusIcons.pending}
        <span>{status || 'Pending'}</span>
      </span>
    );
  };

  const handleTransfer = async () => {
    if (!transferData.recipientEmail.trim() || !transferData.amount.trim() || !transferData.transferReason.trim() || !transferData.transferType.trim()) {
      showError('Please fill in all required fields');
      return;
    }

    const transferAmount = parseFloat(transferData.amount);
    if (transferAmount > availableBalance) {
      showError(`Insufficient balance! You have ${formatCurrency(availableBalance)} but trying to transfer ${formatCurrency(transferAmount)}.`);
      return;
    }

    setIsLoading(true);
    
    try {
      const payload = {
        amount: parseFloat(transferData.amount),
        toEmail: transferData.recipientEmail,
        transferType: transferData.transferType,
        transferDescription: transferData.transferReason,
        remarks: transferData.description || `Transfer of $${transferData.amount} to ${transferData.recipientEmail}`,
        type: "S"
      };

      const response = await apiService.createFundTransfer(payload);
      
      if (response.response && response.response.responseCode === 0) {
        // Success case
        showSuccess(response.response.responseMessage || 'Fund transfer completed successfully');
        
        // Reset form
      setTransferData({
        recipientEmail: '',
        amount: '',
          description: '',
          transferReason: '',
          transferType: ''
      });
      setShowTransferModal(false);
        
        // Refresh transfers list
        setTransferRefresh((n) => n + 1);
      } else {
        // Error case - responseCode is not 0 or response structure is different
        const errorMessage = response.response?.responseMessage || response.responseMessage || 'Transfer failed';
        showError(errorMessage);
      }
    } catch (error) {
      // Error handling
      showError('Failed to process transfer. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCloseModal = () => {
    setShowTransferModal(false);
    setTransferData({
      recipientEmail: '',
      amount: '',
      description: '',
      transferReason: '',
      transferType: ''
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  return (
    <div className={`p-4 ${isDarkMode ? 'bg-black text-white' : 'bg-gray-50 text-gray-900'}`}>
      <div className="mb-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Partner Transfers</h1>
            <p className={`${isDarkMode ? 'text-gray-300' : 'text-gray-600'} mt-1 text-sm`}>Manage money transfers to your partners</p>
          </div>
          <button 
            onClick={() => setShowTransferModal(true)}
            className="bg-red-600 text-white px-3 py-1.5 rounded-lg hover:bg-red-700 transition-colors flex items-center space-x-2 text-sm"
            style={{ backgroundColor: '#8b0000' }}
            onMouseEnter={(e) => e.target.style.backgroundColor = '#7f1d1d'}
            onMouseLeave={(e) => e.target.style.backgroundColor = '#8b0000'}
          >
            <ArrowRight className="h-3 w-3" />
            <span>New Transfer</span>
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="mb-6">
        <div className={`flex space-x-1 p-1 rounded-lg ${isDarkMode ? 'bg-black border border-gray-800' : 'bg-gray-100'}`}>
          <button
            onClick={() => setActiveTab('sent')}
            className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-all duration-200 border ${
              activeTab === 'sent'
                ? isDarkMode 
                  ? 'bg-black text-white shadow-sm border-red-500' 
                  : 'bg-white text-gray-900 shadow-sm border-red-500'
                : isDarkMode
                  ? 'text-gray-400 hover:text-white border-transparent hover:border-gray-600'
                  : 'text-gray-600 hover:text-gray-900 border-transparent hover:border-gray-300'
            }`}
            style={activeTab === 'sent' ? { borderColor: '#8b0000' } : {}}
          >
            Sent Transfers
          </button>
          <button
            onClick={() => setActiveTab('receive')}
            className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-all duration-200 border ${
              activeTab === 'receive'
                ? isDarkMode 
                  ? 'bg-black text-white shadow-sm border-red-500' 
                  : 'bg-white text-gray-900 shadow-sm border-red-500'
                : isDarkMode
                  ? 'text-gray-400 hover:text-white border-transparent hover:border-gray-600'
                  : 'text-gray-600 hover:text-gray-900 border-transparent hover:border-gray-300'
            }`}
            style={activeTab === 'receive' ? { borderColor: '#8b0000' } : {}}
          >
            Receive Transfers
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className={`${isDarkMode ? 'bg-black border-gray-800' : 'bg-white border-gray-200'} rounded-lg shadow-sm border p-4`}>
          <div className="flex items-center">
            <div className="p-1.5 bg-green-100 rounded-lg">
              <DollarSign className="h-5 w-5 text-green-600" />
            </div>
            <div className="ml-3">
              <p className={`text-xs font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-500'}`}>Total Sent</p>
              <p className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{formatCurrency(stats.totalSentAmount)}</p>
            </div>
          </div>
        </div>
        
        <div className={`${isDarkMode ? 'bg-black border-gray-800' : 'bg-white border-gray-200'} rounded-lg shadow-sm border p-4`}>
          <div className="flex items-center">
            <div className="p-1.5 bg-blue-100 rounded-lg">
              <ArrowRight className="h-5 w-5 text-blue-600" />
            </div>
            <div className="ml-3">
              <p className={`text-xs font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-500'}`}>Total Sent Count</p>
              <p className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{stats.sentCount}</p>
            </div>
          </div>
        </div>
        
        <div className={`${isDarkMode ? 'bg-black border-gray-800' : 'bg-white border-gray-200'} rounded-lg shadow-sm border p-4`}>
          <div className="flex items-center">
            <div className="p-1.5 bg-blue-100 rounded-lg">
              <ArrowRight className="h-5 w-5 text-blue-600 rotate-180" />
            </div>
            <div className="ml-3">
              <p className={`text-xs font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-500'}`}>Total Receive</p>
              <p className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{formatCurrency(stats.totalReceiveAmount)}</p>
            </div>
          </div>
        </div>
        
        <div className={`${isDarkMode ? 'bg-black border-gray-800' : 'bg-white border-gray-200'} rounded-lg shadow-sm border p-4`}>
          <div className="flex items-center">
            <div className="p-1.5 bg-green-100 rounded-lg">
              <ArrowRight className="h-5 w-5 text-green-600 rotate-180" />
            </div>
            <div className="ml-3">
              <p className={`text-xs font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-500'}`}>Total Receive Count</p>
              <p className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{stats.receiveCount}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className={`${isDarkMode ? 'bg-black border-gray-800' : 'bg-white border-gray-200'} rounded-lg shadow-sm border p-4 mb-4`}>
        <div className="flex flex-col md:flex-row gap-3">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-3 w-3" />
              <input
                type="text"
                placeholder="Search transfers..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className={`w-full pl-8 pr-10 py-1.5 border rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent text-sm ${
                  isDarkMode 
                    ? 'bg-black border-gray-800 text-white placeholder-gray-400' 
                    : 'border-gray-300 bg-white text-gray-900 placeholder-gray-500'
                }`}
              />
              <button
                onClick={() => setSearchTerm('')}
                className={`absolute right-3 top-1/2 transform -translate-y-1/2 ${isDarkMode ? 'text-gray-400 hover:text-white' : 'text-gray-400 hover:text-gray-600'} transition-colors`}
                title="Clear search"
              >
                <X className="h-3 w-3" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Transfers Table */}
      <div className={`${isDarkMode ? 'bg-black border-gray-800' : 'bg-white border-gray-200'} rounded-lg shadow-sm border overflow-hidden`}>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className={`${isDarkMode ? 'bg-black border-gray-800' : 'bg-gray-50 border-gray-200'} border-b`}>
              <tr>
                <th className={`px-4 py-2 text-left text-xs font-medium uppercase tracking-wider ${
                  isDarkMode ? 'text-gray-300' : 'text-gray-500'
                }`}>
                  Type
                </th>
                <th className={`px-4 py-2 text-left text-xs font-medium uppercase tracking-wider ${
                  isDarkMode ? 'text-gray-300' : 'text-gray-500'
                }`}>
                  Recipient
                </th>
                <th className={`px-4 py-2 text-left text-xs font-medium uppercase tracking-wider ${
                  isDarkMode ? 'text-gray-300' : 'text-gray-500'
                }`}>
                  Amount
                </th>
                <th className={`px-4 py-2 text-left text-xs font-medium uppercase tracking-wider ${
                  isDarkMode ? 'text-gray-300' : 'text-gray-500'
                }`}>
                  Reason
                </th>
                <th className={`px-4 py-2 text-left text-xs font-medium uppercase tracking-wider ${
                  isDarkMode ? 'text-gray-300' : 'text-gray-500'
                }`}>
                  Description
                </th>
                <th className={`px-4 py-2 text-left text-xs font-medium uppercase tracking-wider ${
                  isDarkMode ? 'text-gray-300' : 'text-gray-500'
                }`}>
                  Status
                </th>
                <th className={`px-4 py-2 text-left text-xs font-medium uppercase tracking-wider ${
                  isDarkMode ? 'text-gray-300' : 'text-gray-500'
                }`}>
                  Date
                </th>
                <th className={`px-4 py-2 text-right text-xs font-medium uppercase tracking-wider ${
                  isDarkMode ? 'text-gray-300' : 'text-gray-500'
                }`}>
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className={`${isDarkMode ? 'bg-black divide-gray-800' : 'bg-white divide-gray-200'} divide-y`}>
              {isLoadingTransfers ? (
                <tr>
                  <td colSpan="7" className="px-4 py-8 text-center">
                    <div className="flex items-center justify-center">
                      <NewtonsCradleLoader size="32px" color={isDarkMode ? "#ffffff" : "#000000"} />
                      <span className={`ml-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Loading transfers...</span>
                    </div>
                  </td>
                </tr>
              ) : filteredTransfers.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-4 py-8 text-center">
                    <div className={`${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>No transfers found</div>
                  </td>
                </tr>
              ) : (
                filteredTransfers.map((transfer) => (
                  <tr key={transfer.internalTransferId} className={`${isDarkMode ? 'hover:bg-black' : 'hover:bg-gray-50'}`}>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <div className="flex items-center">
                        <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center mr-2" style={{ backgroundColor: '#8b0000' }}>
                          <span className="text-red-600 font-semibold text-xs" style={{ color: '#ffffff' }}>
                            {activeTab === 'sent' ? 'S' : 'R'}
                          </span>
                        </div>
                        <div>
                          <div className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                            {activeTab === 'sent' ? 'Sent' : 'Received'}
                          </div>
                          <div className={`text-xs ${isDarkMode ? 'text-gray-300' : 'text-gray-500'}`}>
                            {transfer.fromTransferType} → {transfer.toTransferType}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div>
                        <div className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                          {transfer.recipientName || 'N/A'}
                        </div>
                        <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          {transfer.recipientEmail || 'N/A'}
                        </div>
                    </div>
                  </td>
                  <td className={`px-4 py-3 whitespace-nowrap text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                      {formatCurrency(transfer.transferAmount)}
                  </td>
                  <td className={`px-4 py-3 whitespace-nowrap text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                      {transfer.transferReason}
                  </td>
                  <td className="px-4 py-3">
                      <div className={`text-sm ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{transfer.transferRemarks}</div>
                      <div className={`flex items-center space-x-2 text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        <span>ID: {transfer.transactionNumber}</span>
                        <button
                          onClick={() => copyTransactionNumber(transfer.transactionNumber)}
                          className={`${isDarkMode ? 'text-gray-400 hover:text-white' : 'text-gray-400 hover:text-gray-600'} transition-colors`}
                          title="Copy transaction number"
                        >
                          <Copy className="h-3 w-3" />
                        </button>
                      </div>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    {getStatusBadge(transfer.status)}
                  </td>
                  <td className={`px-4 py-3 whitespace-nowrap text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-500'}`}>
                      <div>{new Date(transfer.createdDateTime).toLocaleDateString()}</div>
                      <div className="text-xs">{new Date(transfer.createdDateTime).toLocaleTimeString()}</div>
                  </td>
                  <td className={`px-4 py-3 whitespace-nowrap text-right text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    <div className="flex items-center justify-end space-x-2">
                        <button 
                          onClick={() => handleViewReceipt(transfer)}
                          className={`${isDarkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-400 hover:text-gray-600'} transition-colors`}
                          title="View Receipt"
                        >
                        <Eye className="h-3 w-3" />
                      </button>
                    </div>
                  </td>
                </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      <div className="mt-4 flex items-center justify-between">
        <div className={`text-xs ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
          Showing <span className="font-medium">{((pagination.currentPage - 1) * pagination.pageSize) + 1}</span> to{' '}
          <span className="font-medium">{Math.min(pagination.currentPage * pagination.pageSize, pagination.totalCount)}</span> of{' '}
          <span className="font-medium">{pagination.totalCount}</span> results
        </div>
        <div className="flex space-x-2">
          <button 
            onClick={() => setTransferPage((p) => p - 1)}
            disabled={!pagination.hasPreviousPage}
            className={`px-2 py-1 border rounded text-xs ${
            isDarkMode 
                ? 'border-gray-800 text-gray-300 hover:bg-black disabled:opacity-50 disabled:cursor-not-allowed' 
                : 'border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed'
            }`}
          >
            Previous
          </button>
          <button className="px-2 py-1 bg-red-600 text-white rounded text-xs" style={{ backgroundColor: '#8b0000' }}>
            {pagination.currentPage}
          </button>
          <button 
            onClick={() => setTransferPage((p) => p + 1)}
            disabled={!pagination.hasNextPage}
            className={`px-2 py-1 border rounded text-xs ${
            isDarkMode 
                ? 'border-gray-800 text-gray-300 hover:bg-black disabled:opacity-50 disabled:cursor-not-allowed' 
                : 'border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed'
            }`}
          >
            Next
          </button>
        </div>
      </div>

      {/* New Transfer Modal */}
      {showTransferModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className={`${isDarkMode ? 'bg-black' : 'bg-white'} rounded-lg shadow-xl max-w-xs w-full max-h-[90vh] overflow-y-auto`}>
            {/* Modal Header */}
            <div className={`flex items-center justify-between p-3 border-b ${isDarkMode ? 'border-gray-800' : 'border-gray-200'}`}>
              <h3 className={`text-sm font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>New Transfer</h3>
              <button 
                onClick={handleCloseModal}
                className={`${isDarkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-400 hover:text-gray-600'} transition-colors`}
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-3">
              <div className="mb-3">
                <label htmlFor="email" className={`block text-xs font-medium mb-1 ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                  Recipient Email *
                </label>
                <input
                  type="email"
                  id="email"
                  value={transferData.recipientEmail}
                  onChange={(e) => setTransferData({...transferData, recipientEmail: e.target.value})}
                  placeholder="Enter recipient email address"
                  className={`w-full px-2 py-1.5 border rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent text-sm ${
                    isDarkMode 
                      ? 'bg-black border-gray-800 text-white placeholder-gray-400' 
                      : 'border-gray-300 bg-white text-gray-900 placeholder-gray-500'
                  }`}
                />
              </div>
              
              <div className="mb-3">
                <label htmlFor="transferReason" className={`block text-xs font-medium mb-1 ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                  Transfer Reason *
                </label>
                <select
                  id="transferReason"
                  value={transferData.transferReason}
                  onChange={(e) => setTransferData({...transferData, transferReason: e.target.value})}
                  className={`w-full px-2 py-1.5 border rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent text-sm ${
                    isDarkMode 
                      ? 'bg-black border-gray-800 text-white' 
                      : 'border-gray-300 bg-white text-gray-900'
                  }`}
                >
                  <option value="">Select Transfer Reason</option>
                  <option value="gift">Gift</option>
                  <option value="commission">Commission</option>
                </select>
              </div>
              
              <div className="mb-3">
                <label htmlFor="transferType" className={`block text-xs font-medium mb-1 ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                  Transfer Type *
                </label>
                <select
                  id="transferType"
                  value={transferData.transferType}
                  onChange={(e) => setTransferData({...transferData, transferType: e.target.value})}
                  className={`w-full px-2 py-1.5 border rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent text-sm ${
                    isDarkMode 
                      ? 'bg-black border-gray-800 text-white' 
                      : 'border-gray-300 bg-white text-gray-900'
                  }`}
                >
                  <option value="">Select Transfer Type</option>
                  <option value="Affiliate">Partner</option>
                  <option value="Client">Client</option>
                </select>
              </div>
              
              <div className="mb-3">
                <label htmlFor="amount" className={`block text-xs font-medium mb-1 ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                  Amount (USD) *
                </label>
                <input
                  type="number"
                  id="amount"
                  value={transferData.amount}
                  onChange={(e) => setTransferData({...transferData, amount: e.target.value})}
                  placeholder="0.00"
                  step="0.01"
                  min="0"
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent ${
                    parseFloat(transferData.amount) > availableBalance
                      ? 'border-red-500 bg-red-50 text-red-900'
                      : isDarkMode 
                      ? 'bg-black border-gray-800 text-white placeholder-gray-400' 
                      : 'border-gray-300 bg-white text-gray-900 placeholder-gray-500'
                  }`}
                  style={parseFloat(transferData.amount) > availableBalance ? { borderColor: '#8b0000', backgroundColor: '#8b0000', color: '#ffffff' } : {}}
                />
                <div className={`text-xs mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  Available Balance: <span className={`font-medium ${parseFloat(transferData.amount) > availableBalance ? 'text-red-500' : isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    {transferData.amount ? formatCurrency(availableBalance - (parseFloat(transferData.amount) || 0)) : formatCurrency(availableBalance)}
                  </span>
                </div>
              </div>
              
              <div className="mb-4">
                <label htmlFor="description" className={`block text-xs font-medium mb-1 ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                  Description
                </label>
                <textarea
                  id="description"
                  value={transferData.description}
                  onChange={(e) => setTransferData({...transferData, description: e.target.value})}
                  placeholder="Enter transfer description"
                  rows="2"
                  className={`w-full px-2 py-1.5 border rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent text-sm ${
                    isDarkMode 
                      ? 'bg-black border-gray-800 text-white placeholder-gray-400' 
                      : 'border-gray-300 bg-white text-gray-900 placeholder-gray-500'
                  }`}
                />
              </div>
            </div>

            {/* Modal Footer */}
            <div className={`flex items-center justify-end space-x-3 p-3 border-t ${isDarkMode ? 'border-gray-800' : 'border-gray-200'}`}>
              <button
                onClick={handleCloseModal}
                className={`px-4 py-2 text-sm font-medium border rounded-lg transition-colors ${
                  isDarkMode 
                    ? 'text-gray-200 bg-black border-gray-800 hover:bg-gray-600' 
                    : 'text-gray-700 bg-white border-gray-300 hover:bg-gray-50'
                }`}
              >
                Cancel
              </button>
              <button
                onClick={handleTransfer}
                disabled={isLoading || !transferData.recipientEmail.trim() || !transferData.amount.trim() || !transferData.transferReason.trim() || !transferData.transferType.trim() || (transferData.amount && parseFloat(transferData.amount) > availableBalance)}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                style={{ backgroundColor: '#8b0000' }}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#7f1d1d'}
                onMouseLeave={(e) => e.target.style.backgroundColor = '#8b0000'}
              >
                {isLoading ? (
                  <>
                    <NewtonsCradleLoader size="28px" color="#ffffff" />
                    <span>Processing...</span>
                  </>
                ) : (
                  <>
                    <ArrowRight className="h-4 w-4" />
                    <span>Send Transfer</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Receipt Modal */}
      {showReceiptModal && selectedTransfer && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className={`relative bg-white rounded-xl shadow-xl max-w-sm w-full max-h-[85vh] overflow-y-auto ${
            isDarkMode ? 'bg-black border border-gray-700' : 'bg-white border border-gray-200'
          }`}>
            {/* Receipt Header */}
            <div className={`relative text-center py-4 border-b ${
              isDarkMode ? 'border-gray-700 bg-black' : 'border-gray-200 bg-gray-50'
            }`}>
              <div className={`inline-flex items-center justify-center w-12 h-12 rounded-full mb-2 ${
                isDarkMode ? 'bg-gray-700' : 'bg-red-100'
              }`}>
                <Receipt className={`h-6 w-6 ${isDarkMode ? 'text-gray-300' : 'text-red-600'}`} style={!isDarkMode ? { color: '#8b0000' } : {}} />
              </div>
              <h3 className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                Payment Transfer Receipt
              </h3>
              <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                Transaction Confirmation
              </p>
              <button
                onClick={handleCloseReceiptModal}
                className={`absolute top-3 right-3 p-1 rounded-full transition-colors ${
                  isDarkMode 
                    ? 'text-gray-400 hover:text-white hover:bg-gray-700' 
                    : 'text-gray-400 hover:text-gray-600 hover:bg-gray-200'
                }`}
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            {/* Receipt Body */}
            <div className="p-4 space-y-4">
              {/* Amount Section */}
              <div className={`text-center py-3 border border-dashed rounded-lg ${
                isDarkMode ? 'border-gray-600 bg-black' : 'border-gray-300 bg-gray-50'
              }`}>
                <div className={`text-2xl font-bold mb-1 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  ${selectedTransfer.transferAmount?.toFixed(2)}
                </div>
                <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  TRANSFER AMOUNT
                </div>
                <div className={`mt-2 px-2 py-1 rounded-full text-xs font-medium inline-block ${
                  selectedTransfer.status === 'Approved' 
                    ? 'bg-green-100 text-green-800' 
                    : selectedTransfer.status === 'Pending'
                    ? 'bg-yellow-100 text-yellow-800'
                    : 'bg-red-100 text-red-800'
                }`}>
                  {selectedTransfer.status}
                </div>
              </div>

              {/* Transaction Details */}
              <div className={`p-3 rounded-lg border ${
                isDarkMode ? 'bg-black border-gray-700' : 'bg-white border-gray-200'
              }`}>
                <div className="space-y-2">
                  <div className="flex justify-between items-center py-1">
                    <span className={`text-xs font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Transaction ID</span>
                    <span className={`text-xs font-mono font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                      {selectedTransfer.transactionNumber}
                    </span>
                  </div>
                  <div className="flex justify-between items-center py-1">
                    <span className={`text-xs font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Date & Time</span>
                    <span className={`text-xs font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                      {new Date(selectedTransfer.createdDateTime).toLocaleDateString()} {new Date(selectedTransfer.createdDateTime).toLocaleTimeString()}
                    </span>
                  </div>
                </div>
              </div>

              {/* Transfer Details */}
              <div className={`p-3 rounded-lg border ${
                isDarkMode ? 'bg-black border-gray-700' : 'bg-white border-gray-200'
              }`}>
                <div className="space-y-2">
                  <div className="flex justify-between items-center py-1">
                    <span className={`text-xs font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Transfer Type</span>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      activeTab === 'sent' 
                        ? 'bg-blue-100 text-blue-800' 
                        : 'bg-purple-100 text-purple-800'
                    }`}>
                      {activeTab === 'sent' ? 'Sent' : 'Received'}
                    </span>
                  </div>
                  <div className="flex justify-between items-center py-1">
                    <span className={`text-xs font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>From</span>
                    <span className={`text-xs font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                      {selectedTransfer.fromTransferType}
                    </span>
                  </div>
                  <div className="flex justify-between items-center py-1">
                    <span className={`text-xs font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>To</span>
                    <span className={`text-xs font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                      {selectedTransfer.toTransferType}
                    </span>
                  </div>
                  <div className="flex justify-between items-center py-1">
                    <span className={`text-xs font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Reason</span>
                    <span className={`text-xs font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                      {selectedTransfer.transferReason}
                    </span>
                  </div>
                  <div className="flex justify-between items-start py-1">
                    <span className={`text-xs font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Recipient</span>
                    <div className="text-right">
                      <div className={`text-xs font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                        {selectedTransfer.recipientName || 'N/A'}
                      </div>
                      <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        {selectedTransfer.recipientEmail || 'N/A'}
                      </div>
                    </div>
                  </div>
                  {selectedTransfer.transferRemarks && (
                    <div className="flex justify-between items-start py-1">
                      <span className={`text-xs font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Description</span>
                      <span className={`text-xs font-medium text-right max-w-xs ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                        {selectedTransfer.transferRemarks}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Receipt Footer */}
            <div className={`p-4 border-t ${
              isDarkMode ? 'border-gray-700 bg-black' : 'border-gray-200 bg-gray-50'
            }`}>
              <div className="text-center mb-3">
                <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  Thank you for using our service
                </p>
                <p className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                  Keep this receipt for your records
                </p>
              </div>
              <div className="flex items-center justify-center space-x-2">
                <button
                  onClick={handleCloseReceiptModal}
                  className={`px-4 py-2 text-xs font-medium border rounded-lg transition-colors ${
                    isDarkMode 
                      ? 'text-gray-200 bg-gray-700 border-gray-600 hover:bg-gray-600' 
                      : 'text-gray-700 bg-white border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  Close
                </button>
                <button
                  onClick={() => downloadReceipt(selectedTransfer)}
                  className={`px-4 py-2 text-xs font-medium border rounded-lg transition-colors flex items-center space-x-1 ${
                    isDarkMode 
                      ? 'text-gray-200 bg-gray-700 border-gray-600 hover:bg-gray-600' 
                      : 'text-gray-700 bg-white border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  <Download className="h-3 w-3" />
                  <span>Download</span>
                </button>
                <button
                  onClick={() => copyTransactionNumber(selectedTransfer.transactionNumber)}
                  className="px-4 py-2 text-xs font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors flex items-center space-x-1"
                  style={{ backgroundColor: '#8b0000' }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = '#7f1d1d'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = '#8b0000'}
                >
                  <Copy className="h-3 w-3" />
                  <span>Copy ID</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AffiliateTransfer;
